/** Shared keyboard shortcut definitions for help panel and global handlers. */

export const QUICK_ADD_FOCUS_KEY = 'n';

export const isEditableFocused = () => {
  const activeElement = document.activeElement;
  if (!activeElement || !(activeElement instanceof HTMLElement)) return false;
  return (
    activeElement.tagName === 'INPUT' ||
    activeElement.tagName === 'TEXTAREA' ||
    activeElement.isContentEditable
  );
};

export function isQuickAddFocusShortcut(event) {
  if (event.key !== QUICK_ADD_FOCUS_KEY) return false;
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
    return false;
  }
  return true;
}

export function isGoBarOpenShortcut(event) {
  return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
}

/** @typedef {'global' | 'list' | 'input' | 'other'} AppKeyboardShortcutGroup */

/** @type {AppKeyboardShortcutGroup[]} */
export const APP_KEYBOARD_SHORTCUT_GROUPS = ['global', 'list', 'input', 'other'];

/**
 * @typedef {{
 *   id: string,
 *   group: AppKeyboardShortcutGroup,
 *   descriptionKey: string,
 *   keysKey: string,
 *   keyActionKeys?: string[],
 * }} AppKeyboardShortcut
 */

/** @type {AppKeyboardShortcut[]} */
export const APP_KEYBOARD_SHORTCUTS = [
  {
    id: 'quickAdd',
    group: 'global',
    descriptionKey: 'help.shortcuts.quickAdd',
    keysKey: 'help.shortcuts.keys.quickAdd',
  },
  {
    id: 'goBar',
    group: 'global',
    descriptionKey: 'help.shortcuts.goBar',
    keysKey: 'help.shortcuts.keys.goBar',
  },
  {
    id: 'pageNav',
    group: 'global',
    descriptionKey: 'help.shortcuts.pageNav',
    keysKey: 'help.shortcuts.keys.pageNav',
  },
  {
    id: 'listSelect',
    group: 'list',
    descriptionKey: 'help.shortcuts.listSelect',
    keysKey: 'help.shortcuts.keys.listSelect',
  },
  {
    id: 'listReorder',
    group: 'list',
    descriptionKey: 'help.shortcuts.listReorder',
    keysKey: 'help.shortcuts.keys.listReorder',
  },
  {
    id: 'listComplete',
    group: 'list',
    descriptionKey: 'help.shortcuts.listComplete',
    keysKey: 'help.shortcuts.keys.listComplete',
    keyActionKeys: [' '],
  },
  {
    id: 'listEdit',
    group: 'list',
    descriptionKey: 'help.shortcuts.listEdit',
    keysKey: 'help.shortcuts.keys.listEdit',
    keyActionKeys: ['F2'],
  },
  {
    id: 'listAddAbove',
    group: 'list',
    descriptionKey: 'help.shortcuts.listAddAbove',
    keysKey: 'help.shortcuts.keys.listAddAbove',
    keyActionKeys: ['a'],
  },
  {
    id: 'listAddBelow',
    group: 'list',
    descriptionKey: 'help.shortcuts.listAddBelow',
    keysKey: 'help.shortcuts.keys.listAddBelow',
    keyActionKeys: ['b'],
  },
  {
    id: 'listAddSubtask',
    group: 'list',
    descriptionKey: 'help.shortcuts.listAddSubtask',
    keysKey: 'help.shortcuts.keys.listAddSubtask',
    keyActionKeys: ['s'],
  },
  {
    id: 'listIndent',
    group: 'list',
    descriptionKey: 'help.shortcuts.listIndent',
    keysKey: 'help.shortcuts.keys.listIndent',
    keyActionKeys: ['Ctrl+]'],
  },
  {
    id: 'listOutdent',
    group: 'list',
    descriptionKey: 'help.shortcuts.listOutdent',
    keysKey: 'help.shortcuts.keys.listOutdent',
    keyActionKeys: ['Ctrl+['],
  },
  {
    id: 'listFollowUp',
    group: 'list',
    descriptionKey: 'help.shortcuts.listFollowUp',
    keysKey: 'help.shortcuts.keys.listFollowUp',
    keyActionKeys: ['Ctrl+Space'],
  },
  {
    id: 'listFocus',
    group: 'list',
    descriptionKey: 'help.shortcuts.listFocus',
    keysKey: 'help.shortcuts.keys.listFocus',
    keyActionKeys: ['Ctrl+Enter'],
  },
  {
    id: 'listOpen',
    group: 'list',
    descriptionKey: 'help.shortcuts.listOpen',
    keysKey: 'help.shortcuts.keys.listOpen',
    keyActionKeys: ['Enter'],
  },
  {
    id: 'listDelete',
    group: 'list',
    descriptionKey: 'help.shortcuts.listDelete',
    keysKey: 'help.shortcuts.keys.listDelete',
    keyActionKeys: ['Delete'],
  },
  {
    id: 'listClearSelection',
    group: 'list',
    descriptionKey: 'help.shortcuts.listClearSelection',
    keysKey: 'help.shortcuts.keys.listClearSelection',
    keyActionKeys: ['Escape'],
  },
  {
    id: 'inputSubmit',
    group: 'input',
    descriptionKey: 'help.shortcuts.inputSubmit',
    keysKey: 'help.shortcuts.keys.inputSubmit',
    keyActionKeys: ['Enter'],
  },
  {
    id: 'inputCancel',
    group: 'input',
    descriptionKey: 'help.shortcuts.inputCancel',
    keysKey: 'help.shortcuts.keys.inputCancel',
    keyActionKeys: ['Escape'],
  },
  {
    id: 'goBarNav',
    group: 'other',
    descriptionKey: 'help.shortcuts.goBarNav',
    keysKey: 'help.shortcuts.keys.goBarNav',
  },
  {
    id: 'calendarTaskNav',
    group: 'other',
    descriptionKey: 'help.shortcuts.calendarTaskNav',
    keysKey: 'help.shortcuts.keys.calendarTaskNav',
  },
  {
    id: 'organizeBulkEdit',
    group: 'other',
    descriptionKey: 'help.shortcuts.organizeBulkEdit',
    keysKey: 'help.shortcuts.keys.organizeBulkEdit',
  },
  {
    id: 'mouseMultiSelect',
    group: 'other',
    descriptionKey: 'help.shortcuts.mouseMultiSelect',
    keysKey: 'help.shortcuts.keys.mouseMultiSelect',
  },
  {
    id: 'dashboardBarNav',
    group: 'other',
    descriptionKey: 'help.shortcuts.dashboardBarNav',
    keysKey: 'help.shortcuts.keys.dashboardBarNav',
  },
  {
    id: 'filterBarNav',
    group: 'other',
    descriptionKey: 'help.shortcuts.filterBarNav',
    keysKey: 'help.shortcuts.keys.filterBarNav',
  },
];

/** @param {AppKeyboardShortcutGroup} group */
export function getShortcutsForGroup(group) {
  return APP_KEYBOARD_SHORTCUTS.filter((shortcut) => shortcut.group === group);
}
